import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, KeyRound, Loader2, LogOut, Plus, ShieldCheck, UserCog, UserPlus } from 'lucide-react';
import { Fragment, useState } from 'react';
import { HandoverPassword } from '../components/HandoverPassword';
import { Avatar } from '../components/Avatar';
import { HelpHint } from '../components/HelpHint';
import { PageNav } from '../components/PageNav';
import { RoleAssignment } from '../components/RoleAssignment';
import { Tooltip } from '../components/Hover';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { ROLES, STAFF_ROLES } from '@jt/domain';
import { ScrollX } from '../components/ScrollX';
import { InviteResult } from '../components/InviteResult';
import type { Invite } from '../lib/types';

/** Admin only — the nav hides it, and the API refuses it, independently. */
export function UsersPage() {
  const { user: me } = useSession();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'dispatcher', password: '' });

  /*
   * The roster, for linking a login to the person it belongs to. Loaded here
   * rather than per row: one query for the page instead of one per technician.
   */
  const roster = useQuery({ queryKey: ['technicians-roster'], queryFn: () => api.technicians() });
  const [resetting, setResetting] = useState<string | null>(null);
  /** Whose extra-roles panel is open. One at a time — this is a table. */
  const [assigning, setAssigning] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  /*
   * Deactivated accounts are hidden until asked for.
   *
   * Not cosmetic. An account that has ever done anything cannot be deleted:
   * `audit_events` refuses every UPDATE, and removing a user nulls its
   * `actorId`, which is one. So a deactivated account is permanent — and the
   * verification scripts in `scripts/` left thirty-three of them on this
   * install, against eight real people.
   *
   * A list where the real staff are outnumbered four to one by accounts that
   * cannot log in and cannot be removed is a list nobody reads. They are still
   * one click away, because "who used to have access" is a real question and
   * the honest answer to it is the reason those rows are kept at all.
   */
  const [showRetired, setShowRetired] = useState(false);
  /**
   * What to hand over, kept on screen until the admin closes it.
   *
   * A dialog rather than a panel in the page. The panel was easy to walk past —
   * the form closed, the table refreshed underneath it, and the one thing that
   * mattered was a box somewhere above the fold. Handing somebody an account is
   * a step that is either finished or not, so it interrupts.
   */
  const [handover, setHandover] = useState<{
    title: string;
    person: { name: string; email: string };
    invite: Invite;
    password: string;
  } | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: api.users });

  const refresh = async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const create = useMutation({
    mutationFn: () => api.createUser(form),
    onSuccess: async (created) => {
      setHandover({
        title: 'Account created',
        person: { name: form.name, email: form.email },
        invite: created.invite,
        password: form.password,
      });
      setAdding(false);
      setForm({ name: '', email: '', role: 'dispatcher', password: '' });
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; body: Record<string, unknown> }) =>
      api.updateUser(vars.id, vars.body),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  /*
   * Sign somebody out of everything (ADR-038) — a lost phone, a suspected
   * compromise, somebody leaving. They can sign in again; switching the
   * account off is what stops that.
   */
  const endSessions = useMutation({
    mutationFn: (id: string) => api.endUserSessions(id),
    onSuccess: ({ ended }, id) => {
      const account = users.data?.users.find((row) => row.id === id);
      setError(null);
      window.alert(
        `${account?.name ?? 'That account'} was signed out of ${ended} ` +
          `${ended === 1 ? 'session' : 'sessions'}.`,
      );
    },
    onError: (e: Error) => setError(e.message),
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) => api.resetUserPassword(id, newPassword),
    onSuccess: async (result, id) => {
      const account = users.data?.users.find((row) => row.id === id);
      setHandover({
        title: 'Password reset',
        person: { name: account?.name ?? 'That account', email: account?.email ?? '' },
        invite: result.invite,
        password: newPassword,
      });
      setResetting(null);
      setNewPassword('');
      await refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (users.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const everyone = users.data?.users ?? [];
  const retired = everyone.filter((row) => !row.isActive).length;
  const rows = showRetired ? everyone : everyone.filter((row) => row.isActive);
  const owing = rows.filter((row) => row.mustChangePassword && row.isActive);

  return (
    /*
     * Wide enough for the table, which is what this page mostly is.
     *
     * It was `max-w-3xl` — a 768px reading column. That is the right width for
     * prose and the wrong one for ten columns of user: the table overflowed by
     * 168px and grew a scrollbar while 330px of the window sat unused either
     * side of it. Filling the space costs nothing and removes the scrollbar
     * entirely at this size; the paragraphs on the page carry their own
     * `max-w-2xl` so they stay readable rather than stretching to the edges.
     */
    <div className="mx-auto max-w-6xl space-y-5">
      <PageNav crumbs={[{ label: 'Users' }]} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 font-heading text-2xl">
            Users
            <HelpHint>
              Everyone who signs in — admins, dispatchers, and technicians who have been given an
              account. A technician can also work from a job link with no account at all.
            </HelpHint>
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {everyone.filter((u) => u.isActive).length} active
            {retired > 0 && (
              <>
                {' · '}
                {retired} deactivated
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/*
            * Only offered when there is something to show. A toggle for a list
            * that is already complete is a control that does nothing, and a
            * control that does nothing teaches people to ignore the ones that
            * do.
            */}
          {retired > 0 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowRetired((on) => !on)}
              aria-pressed={showRetired}
            >
              {showRetired ? 'Hide deactivated' : `Show ${retired} deactivated`}
            </button>
          )}
          <button type="button" onClick={() => setAdding((v) => !v)} className="btn-primary">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add user
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-600">
          {error}
        </p>
      )}

      {/*
        Shown once, after the fact, over the page rather than in it — with the
        invitation link, which is the part an admin actually needs and the part
        this screen never used to show.
      */}
      {handover && (
        <InviteResult
          title={handover.title}
          person={handover.person}
          invite={handover.invite}
          password={handover.password}
          onClose={() => setHandover(null)}
        />
      )}

      {/*
        A standing count, separate from the one-time card above. An account
        still on its handover password is a password two people know, and until
        it is on this screen it is invisible.
      */}
      {owing.length > 0 && !handover && (
        <p className="flex items-start gap-2 rounded-xl bg-accent-50 p-3 text-sm text-accent-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {owing.length === 1
            ? `${owing[0]!.name} has not chosen their own password yet — the one you gave them is still live.`
            : `${owing.length} people have not chosen their own password yet. Those handover passwords are still live.`}
        </p>
      )}

      {adding && (
        <form
          className="card space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <h3 className="font-heading text-lg">New user</h3>

          <div>
            <label htmlFor="name" className="label">
              Name
            </label>
            <input
              id="name"
              required
              minLength={2}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-field"
            />
          </div>

          <div>
            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="name@gospeednet.co.ke"
              className="input-field"
            />
          </div>

          <div>
            <label htmlFor="role" className="label">
              Role
              <HelpHint>
                A dispatcher can run the day but cannot change the rules the day is measured
                against — settings, users, SLA policies and the audit log are admin only. A
                technician sees only their own jobs, attendance and expenses.
              </HelpHint>
            </label>
            <select
              id="role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="input-field"
            >
              {/*
                Every job this business has, from the domain rather than from a
                list typed here.

                It offered three — dispatcher, technician, admin — and had done
                since before sales, HR and accounting existed. The roles were
                real, the permissions were real, the row below could even change
                somebody into one; they simply could not be *hired* as one. A
                hardcoded list beside a live one always ends this way.

                `STAFF_ROLES` is every role now that customers hold no
                account. The alias is kept rather than inlined: the day a
                non-staff role exists again, this list should not silently
                start offering it.
              */}
              {STAFF_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role[0]!.toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
            {form.role === 'technician' && (
              <p className="mt-1 text-xs text-muted-foreground">
                Link the account to a technician record on the Technicians page afterwards,
                otherwise &ldquo;my jobs&rdquo; has nothing to filter on.
              </p>
            )}
            {/*
              Why there is no 'Client' in the list above.

              There is no customer account any more, anywhere. The portal was
              removed deliberately: a customer receives their contract as a link
              in an email and signs it there, which needs no login at all.

              This note stays because the question does. It read as an oversight
              when the omission was silent, and was reported as one — twice.
            */}
            <p className="mt-2 text-xs text-muted-foreground">
              Customers do not sign in. They receive their contract as a link by email and
              sign it there, so there is no customer account to create — here or anywhere.
            </p>
          </div>

          <HandoverPassword
            value={form.password}
            onChange={(password) => setForm({ ...form, password })}
            context={{ email: form.email, name: form.name }}
          />

          <button type="submit" disabled={create.isPending} className="btn-primary w-full">
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Create user
          </button>
        </form>
      )}

      <ScrollX className="rounded-2xl border border-border bg-card shadow-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Password</th>
              <th className="px-4 py-3 font-semibold">Last signed in</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <Fragment key={row.id}>
              <tr className={row.isActive ? '' : 'opacity-55'}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <Avatar userId={row.id} name={row.name} size="md" />
                    <div className="min-w-0">
                      <span className="font-medium">{row.name}</span>
                      {row.id === me?.id && (
                        <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                      )}
                      <div className="truncate text-xs text-muted-foreground">{row.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {/*
                    The role is editable here, and it was not.
                    ----------------------------------------
                    This column rendered a badge. The API has always accepted a
                    new primary role on PATCH /users/:id — every one of the five
                    changes cleanly — but there was no way to ask for it from
                    the screen, so somebody hired as a dispatcher stayed a
                    dispatcher forever unless an administrator went to the API
                    directly. The extra-roles panel below adds a *second* job;
                    it cannot change the first, and the two were easy to
                    conflate.

                    Disabled for your own admin account, because the API refuses
                    that and a control that always errors is worse than one that
                    is visibly unavailable. The last-admin rule is enforced
                    server-side and its refusal is shown as written.
                  */}
                  <div className="flex items-center gap-1.5">
                    {row.role === 'admin' && (
                      <ShieldCheck
                        className="h-3.5 w-3.5 shrink-0 text-primary-700"
                        aria-hidden="true"
                      />
                    )}
                    <select
                      className="input h-8 min-w-32 py-0 text-sm"
                      value={row.role}
                      disabled={update.isPending || (row.id === me?.id && row.role === 'admin')}
                      aria-label={`Role for ${row.name}`}
                      onChange={(event) =>
                        update.mutate({ id: row.id, body: { role: event.target.value } })
                      }
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>

                  {row.id === me?.id && row.role === 'admin' && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      You cannot change your own admin role.
                    </p>
                  )}

                  {/*
                    Which person on the roster this login is.

                    Two halves of one person that nothing could join until now:
                    adding somebody to the roster made a technician record,
                    creating their login made a user, and neither knew about the
                    other. A technician whose login is not linked has no work of
                    their own, so the list that shows "your jobs" has nothing to
                    narrow by — which is why it is said loudly here rather than
                    left to be discovered.
                  */}
                  {row.role === 'technician' && (
                    <div className="mt-1.5">
                      <select
                        className="input h-8 min-w-40 py-0 text-xs"
                        value={row.technicianId ?? ''}
                        disabled={update.isPending}
                        aria-label={`Roster record for ${row.name}`}
                        onChange={(event) =>
                          update.mutate({
                            id: row.id,
                            body: { technicianId: event.target.value || null },
                          })
                        }
                      >
                        <option value="">Not on the roster</option>
                        {(roster.data?.technicians ?? []).map((tech) => (
                          <option key={tech.id} value={tech.id}>
                            {tech.name}
                          </option>
                        ))}
                      </select>
                      {!row.technicianId && (
                        <p className="mt-1 flex items-start gap-1 text-xs text-accent-800">
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                          Not linked, so they see no tickets at all. Pick their name above.
                        </p>
                      )}
                    </div>
                  )}

                  {!row.isActive && (
                    <span className="badge mt-1 bg-muted text-muted-foreground">disabled</span>
                  )}

                  {/*
                    The extra jobs, on the row.
                    With additional roles in play the role column alone no longer
                    answers "who can do X", which is the question this screen is
                    usually open for. A quieter badge than the primary role, and
                    the word "also" so it is not read as a second job title.
                  */}
                  {row.extraRoles.length > 0 && (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <span className="text-[11px] text-muted-foreground">also</span>
                      {row.extraRoles.map((extra) => (
                        <span
                          key={extra}
                          className="badge border border-border bg-card text-muted-foreground"
                        >
                          {extra}
                        </span>
                      ))}
                    </div>
                  )}

                  {row.groups.length > 0 && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {row.groups.join(' · ')}
                    </div>
                  )}
                </td>
                {/*
                  Colour is not the signal here — the words say it. Somebody who
                  cannot tell amber from grey still reads "handover, not
                  changed".
                */}
                <td className="whitespace-nowrap px-4 py-3">
                  {row.mustChangePassword ? (
                    <span className="badge bg-accent-50 text-accent-700">
                      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                      handover, not changed
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {row.passwordChangedAt
                        ? `theirs since ${new Date(row.passwordChangedAt).toLocaleDateString('en-KE')}`
                        : 'theirs'}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                  {row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleDateString('en-KE') : 'never'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Tooltip label={`Roles and groups for ${row.name}`} side="left">
                      <button
                        type="button"
                        onClick={() => {
                          setAssigning(assigning === row.id ? null : row.id);
                          setResetting(null);
                        }}
                        className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                        aria-expanded={assigning === row.id}
                      >
                        <UserPlus className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">Roles and groups for {row.name}</span>
                      </button>
                    </Tooltip>
                    <button
                      type="button"
                      title="Reset password"
                      onClick={() => {
                        setResetting(resetting === row.id ? null : row.id);
                        setNewPassword('');
                      }}
                      className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                    >
                      <KeyRound className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Reset {row.name}&apos;s password</span>
                    </button>
                    <button
                      type="button"
                      title={`Sign ${row.name} out everywhere`}
                      disabled={endSessions.isPending || !row.isActive}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Sign ${row.name} out of every device now? They can sign in again straight away.`,
                          )
                        ) {
                          endSessions.mutate(row.id);
                        }
                      }}
                      className="rounded-lg p-2 text-muted-foreground hover:bg-muted disabled:opacity-30"
                    >
                      <LogOut className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Sign {row.name} out everywhere</span>
                    </button>
                    <button
                      type="button"
                      /*
                       * The reason, not just the verb. Disabled and silent, the
                       * only thing this told anybody was that something was
                       * wrong with the button.
                       */
                      title={
                        row.id === me?.id
                          ? 'You cannot disable your own account — ask another administrator'
                          : row.isActive
                            ? `Disable ${row.name}`
                            : `Enable ${row.name}`
                      }
                      disabled={row.id === me?.id || update.isPending}
                      onClick={() =>
                        update.mutate({ id: row.id, body: { isActive: !row.isActive } })
                      }
                      className="rounded-lg p-2 text-muted-foreground hover:bg-muted disabled:opacity-30"
                    >
                      <UserCog className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">
                        {row.isActive ? 'Disable' : 'Enable'} {row.name}
                      </span>
                    </button>
                  </div>

                  {resetting === row.id && (
                    <div className="mt-2 min-w-[18rem] text-left">
                      <HandoverPassword
                        value={newPassword}
                        onChange={setNewPassword}
                        context={{ email: row.email, name: row.name }}
                        label={`New password for ${row.name}`}
                      />
                      <button
                        type="button"
                        disabled={!newPassword || resetPassword.isPending}
                        onClick={() => resetPassword.mutate(row.id)}
                        className="btn-primary mt-2 w-full py-1.5 text-sm disabled:opacity-40"
                      >
                        {resetPassword.isPending && (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        )}
                        Set it
                      </button>
                    </div>
                  )}
                </td>
              </tr>

              {/*
                The roles panel gets a row of its own, spanning the table.

                It first lived in the actions cell, which was wrong twice over
                and only a screenshot showed it. The cell is the narrowest
                column in the table, so a 22rem panel ran past the right edge
                and was clipped by the wrapper's `overflow-x-auto` — the Add
                buttons were off screen. And the cell belongs to a row that
                carries `opacity-55` when the account is disabled, so reading
                somebody's roles meant reading them through a fade.

                A full-width row below theirs fixes both, and puts the panel
                where the eye already expects an expansion to appear.
              */}
              {assigning === row.id && (
                <tr>
                  <td colSpan={5} className="bg-muted/40 px-4 pb-4 pt-1">
                    <p className="mb-2 text-sm font-medium">
                      Roles and groups for {row.name}
                    </p>
                    <div className="max-w-xl rounded-xl border border-border bg-card p-4">
                      <RoleAssignment userId={row.id} name={row.name} />
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </ScrollX>

      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          Disabling a user ends their access immediately. Resetting a password also signs them out
          everywhere — a reset that leaves the old session alive is not a reset.
        </p>
        <p>
          Everyone can change their own password from <strong>Your account</strong> in the top bar,
          without asking you. Reset one here only when somebody is locked out — every reset means a
          password two people know, which is why they are made to replace it on the way in.
        </p>
      </div>
    </div>
  );
}
